import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { Alert, Box, Button, CircularProgress, Divider, TextField, Typography } from '@mui/material';
import logo from '../assets/ponto-now-logo.png';
import './Login.css';

import AccountAccessNotice from '../components/AccountAccessNotice';
import { API_BASE_URL } from '../config/apiBase';
import { clearAdminSession, googleAuthOutcome } from '../utils/accountAccess';
import { hasUsableAdminToken } from '../utils/adminSession';

const safeReturnPath = (value) => (
  typeof value === 'string' && value.startsWith('/') && !value.startsWith('//') && !value.startsWith('/login')
    ? value
    : '/dashboard'
);

const Login = () => {
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [gate, setGate] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const returnPath = safeReturnPath(location.state?.from);
  const [alreadySignedIn] = useState(() => hasUsableAdminToken(localStorage.getItem('token')));

  // Debug: verificar se o componente está montado e se o Google está carregado
  useEffect(() => {
    // Verificar se o script do Google está carregado
    const checkGoogleScript = setInterval(() => {
      if (window.google && window.google.accounts) {
        console.log('✅ Google OAuth script carregado');
        clearInterval(checkGoogleScript);
      } else {
        console.warn('⏳ Aguardando carregamento do Google OAuth script...');
      }
    }, 500);

    // Limpar após 10 segundos
    setTimeout(() => {
      clearInterval(checkGoogleScript);
      if (!window.google || !window.google.accounts) {
        console.error('❌ Google OAuth script não carregado após 10 segundos');
        setError('Erro ao carregar o serviço de autenticação do Google. Recarregue a página.');
      }
    }, 10000);

    return () => clearInterval(checkGoogleScript);
  }, []);

  const persistSession = (token, user) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
  };

  const showGate = (kind, email) => {
    clearAdminSession();
    setGate({ kind, email: email || '' });
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API_BASE_URL}/auth/login`, {
        email,
        password: senha,
      });

      const outcome = googleAuthOutcome(response.status, response.data);
      if (outcome === 'pending' || outcome === 'rejected') {
        showGate(outcome, response.data?.email || email);
        return;
      }
      persistSession(response.data.token, response.data.user);
      navigate(returnPath, { replace: true });
    } catch (err) {
      const outcome = googleAuthOutcome(err.response?.status, err.response?.data);
      if (outcome === 'pending' || outcome === 'rejected') {
        showGate(outcome, err.response?.data?.email || email);
        return;
      }
      setError(err.response?.data?.message || 'Erro ao efetuar login. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    const credential = credentialResponse?.credential;
    if (!credential) {
      setError('Não foi possível validar o token do Google.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API_BASE_URL}/auth/google`, {
        idToken: credential,
      }, {
        timeout: 15000,
      });

      const outcome = googleAuthOutcome(response.status, response.data);
      if (outcome === 'pending' || outcome === 'rejected') {
        showGate(outcome, response.data?.email);
        return;
      }
      if (!response.data?.token) {
        setError('Falha ao autenticar com Google.');
        return;
      }
      persistSession(response.data.token, response.data.user);
      navigate(returnPath, { replace: true });
    } catch (err) {
      console.error('Erro ao autenticar com Google:', err);
      const outcome = googleAuthOutcome(err.response?.status, err.response?.data);
      if (outcome === 'pending' || outcome === 'rejected') {
        showGate(outcome, err.response?.data?.email);
        return;
      }
      const message = err.code === 'ECONNABORTED'
        ? 'O servidor não respondeu ao login com Google. Tente novamente.'
        : (err.response?.data?.message || 'Falha ao autenticar com Google.');
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    console.error('Erro no login do Google');
    setError('Não foi possível concluir o login com Google.');
  };

  if (alreadySignedIn) {
    return <Navigate to={returnPath} replace />;
  }

  return (
    <Box className="login-wrapper">
      <Box className="brand-panel">
        <img src={logo} alt="Logotipo Ponto Now" className="brand-logo" />
        <Typography variant="h3" component="h1" className="brand-title">
          Ponto Now
        </Typography>
        <Typography variant="subtitle1" className="brand-subtitle">
          Controle inteligente de ponto, com segurança e praticidade.
        </Typography>
      </Box>

      <Box component="section" className="login-card">
        {gate ? (
          <AccountAccessNotice
            kind={gate.kind}
            email={gate.email}
            onBack={() => { setGate(null); setError(null); }}
          />
        ) : (
          <>
        <Typography variant="h5" className="login-heading">
          Acesse sua conta
        </Typography>
        <Typography variant="body2" className="login-subheading">
          Utilize suas credenciais corporativas ou continue com Google.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ width: '100%', mt: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" className="login-form" onSubmit={handleLogin}>
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            fullWidth
            required
            margin="normal"
            color="primary"
            variant="outlined"
            InputProps={{ className: 'input-field' }}
          />

          <TextField
            label="Senha"
            type="password"
            value={senha}
            onChange={(event) => setSenha(event.target.value)}
            fullWidth
            required
            margin="normal"
            color="primary"
            variant="outlined"
            InputProps={{ className: 'input-field' }}
          />

          <Button
            type="submit"
            variant="contained"
            size="large"
            className="login-button"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Entrar'}
          </Button>
        </Box>

        <Divider className="divider">ou continue com</Divider>

        <Box className="google-button-wrapper">
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
            shape="pill"
            theme="filled_black"
            size="large"
            text="signin_with"
            useOneTap={false}
            auto_select={false}
          />
        </Box>

          </>
        )}

        <Button variant="text" onClick={() => navigate('/hackathon')} sx={{ mt: 2 }}>
          Sou participante do hackathon
        </Button>

        <Typography variant="caption" className="login-disclaimer">
          Ao entrar você concorda com nossos termos de uso e políticas de privacidade.
        </Typography>
      </Box>
    </Box>
  );
};

export default Login;

